/**
 * Global Product Card Logic
 * Handles swatch interactions (hover) for product cards across all pages.
 * Uses event delegation to support dynamically loaded content (infinite scroll, filters, etc).
 */

(function() {
  // Config
  const SELECTORS = {
    CARD: '.insy-card',
    SWATCH_ITEM: '.insy-swatch-item',
    SWATCHES_CONTAINER: '.insy-swatches',
    IMAGE_PRIMARY: '.insy-img.is-primary',
    IMAGE_SECONDARY: '.insy-img.is-secondary',
    IMAGE_VARIANT: '.insy-img.is-variant',
    PRICE_CONTAINER: '.js-card-price',
    QUICK_ADD_BTN: '.quick-add-btn'
  };

  const CLASSES = {
    ACTIVE: 'active',
    VARIANT_ACTIVE: 'variant-active'
  };

  /**
   * Updates the product card state based on selected swatch
   * @param {HTMLElement} card - The product card element
   * @param {HTMLElement} swatch - The hovered/clicked swatch element
   */
  function updateCardState(card, swatch) {
    if (!card || !swatch) return;

    // Get data attributes
    const variantId = swatch.dataset.variantId;
    const mediaId = swatch.dataset.mediaId;
    const imageUrl = swatch.dataset.imageUrl;
    const price = swatch.dataset.price;
    const compareAtPrice = swatch.dataset.compareAtPrice;

    // 1. Update Active Swatch UI
    const allSwatches = card.querySelectorAll(SELECTORS.SWATCH_ITEM);
    allSwatches.forEach(s => s.classList.remove(CLASSES.ACTIVE));
    swatch.classList.add(CLASSES.ACTIVE);

    // 2. Update Product Image
    const variantImg = card.querySelector(`${SELECTORS.IMAGE_VARIANT}[data-media-id="${mediaId}"]`);
    
    if (variantImg) {
      // Swatch has a specific variant image present in the DOM
      card.classList.add(CLASSES.VARIANT_ACTIVE);
      
      // Reset other variant images
      card.querySelectorAll(SELECTORS.IMAGE_VARIANT).forEach(img => {
        img.classList.remove(CLASSES.ACTIVE);
        // Ensure inline styles don't override class-based toggling if used differently elsewhere
        // But our CSS uses !important for .variant-active .is-variant.active, so we just toggle class
      });
      
      variantImg.classList.add(CLASSES.ACTIVE);
    } else if (imageUrl) {
      // Fallback: Swatch has an image URL but no pre-rendered DOM element (e.g. Color mode without preloading)
      // Or simply update the primary image if that's the desired behavior for "Color" mode
      const primaryImg = card.querySelector(SELECTORS.IMAGE_PRIMARY);
      if (primaryImg) {
        // Only update if we actually have a url
        // Ideally we would want to swap back on mouseout, but "sticky" hover is often preferred for commerce
        // primaryImg.src = imageUrl; 
        // Note: Changing src directly might break lazy loading or resolution switching if not careful.
        // For safe implementation without 'variant-active' class mechanics, we might just leave it 
        // or check if the user wants this. The original script had this logic.
        primaryImg.src = imageUrl;
        if (primaryImg.srcset) primaryImg.srcset = imageUrl;
      }
    }

    // 3. Update Add to Cart Button Variant ID and Quantity Min
    const atcBtn = card.querySelector(SELECTORS.QUICK_ADD_BTN);
    if (atcBtn && swatch.dataset.quantityMin) {
      atcBtn.dataset.variant = variantId;
      atcBtn.dataset.quantityMin = swatch.dataset.quantityMin;
    } else if (atcBtn) {
      atcBtn.dataset.variant = variantId;
    }

    // 4. Update Price
    const priceContainer = card.querySelector(SELECTORS.PRICE_CONTAINER);
    if (priceContainer && price) {
      // Simple fade effect
      priceContainer.style.transition = 'opacity 0.2s ease';
      priceContainer.style.opacity = '0.4';

      // Update text after short delay to smooth transition
      requestAnimationFrame(() => {
        let priceHTML = '';
        if (compareAtPrice) {
          priceHTML += `<span class="compare-price" style="text-decoration: line-through; opacity: 0.6; margin-right: 6px;">${compareAtPrice}</span>`;
        }
        priceHTML += `<span class="current-price">${price}</span>`;
        
        priceContainer.innerHTML = priceHTML;
        priceContainer.style.opacity = '1';
      });
    }
  }

  // Event Delegation for Hover (Mouseover) using 'mouseover' which bubbles
  // 'mouseenter' does NOT bubble, so we must use 'mouseover'
  document.addEventListener('mouseover', function(e) {
    const swatch = e.target.closest(SELECTORS.SWATCH_ITEM);
    
    // Only proceed if we are genuinely entering a swatch we haven't processed yet (or just re-process)
    if (!swatch) return;

    // Check if this swatch is already active to avoid unnecessary DOM updates (performance)
    if (swatch.classList.contains(CLASSES.ACTIVE)) return;

    const card = swatch.closest(SELECTORS.CARD);
    if (card) {
      updateCardState(card, swatch);
    }
  });

  // Optional: Reset logic. 
  // The original script didn't strictly reset on mouseout ("User didn't specify reset behavior. Standard is stick.").
  // We will maintain "sticky" behavior where the last hovered swatch stays active until another is hovered. 
  // This is better for UX so user can see what they just hovered over while moving mouse to "Add to Cart".

  console.log('Global product card swatch logic initialized.');

})();